import React, { useMemo } from 'react';

import { Empty } from 'antd';
import type { TooltipComponentOption } from 'echarts';
import ReactECharts, { type EChartsOption } from 'echarts-for-react';

import { COLOR, FONT, RADIUS, SPACE } from '@/shared/design-tokens/design-tokens';
import { formatNumberWithK } from '@/shared/lib/utils/number';

import styles from './BarChart.module.css';

export type BarChartProps = {
  data: Record<string, unknown>[];
  showLoading: boolean;
  xAxisKey: string;
  yAxisName?: string;
  selectedSeries?: Record<string, boolean>;
  isLegendVisible?: boolean;
  emptyDescription?: string;
  colorMap?: Record<string, string>;
  containerStyle?: React.CSSProperties;
  customTooltipFormatter?: (params: TooltipComponentOption) => string;
};

const formatAxisTooltip = (params: TooltipComponentOption, axisKey: string): string => {
  if (Array.isArray(params)) {
    const firstParam = params[0] as { axisValue: string; data: Record<string, unknown> };
    let result = '';
    const firstParamData = firstParam.data;

    Object.entries(firstParamData).forEach(([key, value]) => {
      if (key === axisKey) {
        result += `${value}<br/>`;
      } else {
        result += `${key}: ${value}<br/>`;
      }
    });
    return result;
  }
  return '';
};

/**
 * @param data - 데이터
 * @param xAxisKey - x축 키
 * @param yAxisName - y축 이름
 * @param isLegendVisible - 범례 표시 여부
 * @param showLoading - 로딩 표시 여부
 * @param emptyDescription - 빈 데이터 설명
 * @param colorMap - 시리즈별 색상 맵
 * @param containerStyle - 컨테이너 스타일
 * @param selectedSeries - 선택된 시리즈
 * @param customTooltipFormatter - 툴팁 포맷터
 * @param props - 추가 프로퍼티
 */

export const BarChart = React.memo(
  ({
    data,
    xAxisKey,
    yAxisName,
    selectedSeries,
    customTooltipFormatter,
    emptyDescription = '데이터가 없습니다',
    showLoading = false,
    isLegendVisible = true,
    colorMap,
    containerStyle,
    ...props
  }: BarChartProps) => {
    const isEmpty = data.length === 0 && !showLoading;
    const xAxisData = useMemo(() => data.map((item) => item[xAxisKey]), [data, xAxisKey]);

    const yAxisKeys = useMemo(() => {
      if (!data[0]) return [];
      return Object.keys(data[0]).filter((key) => key !== xAxisKey);
    }, [data, xAxisKey]);

    const chartOption: EChartsOption = useMemo(
      () => ({
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          renderMode: 'html',
          formatter: (params: TooltipComponentOption) =>
            customTooltipFormatter ? customTooltipFormatter(params) : formatAxisTooltip(params, xAxisKey),
          textStyle: {
            fontSize: FONT.SIZE.SM,
          },
        },
        legend: {
          show: isLegendVisible,
          bottom: 0,
          left: 'right',
          top: 0,
          orient: 'horizontal',
          icon: 'circle',
          textStyle: {
            fontFamily: FONT.FAMILY.NANUMSQUARE,
            fontWeight: FONT.WEIGHT.REGULAR,
            fontSize: FONT.SIZE.XS,
            lineHeight: FONT.LINE_HEIGHT[16],
            color: COLOR.GRAY[50],
          },
          itemGap: SPACE[12],
          itemWidth: SPACE[8],
          itemHeight: SPACE[8],
          selected: selectedSeries || {},
          selectedMode: selectedSeries ? false : true,
        },
        grid: {
          left: 0,
          right: 0,
          top: 38,
          bottom: 0,
        },
        xAxis: {
          type: 'category',
          data: xAxisData,
          axisLabel: {
            fontFamily: FONT.FAMILY.NANUMSQUARE,
            fontWeight: FONT.WEIGHT.REGULAR,
            fontSize: FONT.SIZE.XS,
            lineHeight: FONT.LINE_HEIGHT[16],
            color: COLOR.GRAY[50],
            align: 'center',
            verticalAlign: 'middle',
            margin: SPACE[24],
          },
          axisLine: {
            lineStyle: {
              color: COLOR.GRAY[90],
            },
          },
          axisTick: {
            lineStyle: {
              color: COLOR.GRAY[90],
            },
          },
          splitLine: {
            show: false,
            lineStyle: {
              color: COLOR.GRAY[90],
              type: 'solid',
            },
          },
        },
        yAxis: {
          type: 'value',
          name: yAxisName,
          nameLocation: 'middle',
          nameTextStyle: {
            fontFamily: FONT.FAMILY.NANUMSQUARE,
            fontWeight: FONT.WEIGHT.REGULAR,
            fontSize: FONT.SIZE.XS,
            lineHeight: FONT.LINE_HEIGHT[16],
            color: COLOR.GRAY[50],
            align: 'center',
            verticalAlign: 'middle',
          },
          axisLabel: {
            fontFamily: FONT.FAMILY.NANUMSQUARE,
            fontWeight: FONT.WEIGHT.REGULAR,
            fontSize: FONT.SIZE.XS,
            lineHeight: FONT.LINE_HEIGHT[16],
            color: COLOR.GRAY[50],
            align: 'center',
            verticalAlign: 'middle',
            formatter: formatNumberWithK,
            margin: SPACE[24],
          },
          axisLine: {
            lineStyle: {
              color: COLOR.GRAY[90],
              type: 'dashed',
            },
          },
          axisTick: {
            lineStyle: {
              color: COLOR.GRAY[90],
            },
          },
          splitLine: {
            lineStyle: {
              color: COLOR.GRAY[90],
              type: 'dashed',
            },
          },
        },
        dataset: {
          source: data,
        },
        series: yAxisKeys.map((key) => ({
          name: key,
          type: 'bar',
          encode: {
            x: xAxisKey,
            y: key,
          },
          itemStyle: {
            color: colorMap?.[key],
            borderRadius: [RADIUS[4], RADIUS[4], 0, 0],
          },
        })),
      }),
      [
        isLegendVisible,
        selectedSeries,
        xAxisData,
        yAxisName,
        data,
        yAxisKeys,
        customTooltipFormatter,
        xAxisKey,
        colorMap,
      ]
    );

    return (
      <div className={isEmpty ? styles.emptyContainer : styles.chartContainer} style={containerStyle}>
        {isEmpty && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />}
        <ReactECharts
          option={chartOption}
          showLoading={showLoading}
          lazyUpdate={true}
          style={{ height: '100%' }}
          className={`${isEmpty && styles.hidden}`}
          {...props}
        />
      </div>
    );
  }
);

BarChart.displayName = 'BarChart';
